import React, { useState } from "react";
import { DayPicker, DateRange, getDefaultClassNames } from "react-day-picker";
import { DatePickerStepProps } from "../../../../types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { RootState } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { DateTimeUtil } from "../../../../utils";
import { format } from "date-fns";

const DatePickerStep: React.FC<DatePickerStepProps> = ({
    selectedDates,
    setSelectedDates,
    onNext,
    isUpdateMode,
    timeoffToUpdate,
}) => {
    const [currentMonth, setCurrentMonth] = useState(() => new Date());

    const handleSelect = (range: DateRange | undefined) => {
        if (!range) return;
        const { from, to } = range;

        // Update state with the range
        if (from) {
            const dates = to ? [from, to] : [from]; // Handle incomplete ranges
            setSelectedDates(dates);
        }
    };

    const handleClearSelection = () => {
        setSelectedDates([]); // Clear the selection
    };

    const CalendarHeader = ({ month, onMonthChange }: { month: Date; onMonthChange: (date: Date) => void }) => {
        const buttonSize = 28;
        const goToPrevMonth = () => {
            const prev = new Date(month.getFullYear(), month.getMonth() - 1, 1);
            onMonthChange(prev);
        };

        const goToNextMonth = () => {
            const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
            onMonthChange(next);
        };

        return (
            <div className="flex items-center justify-between px-4 py-2 bg-white">
                <button onClick={goToPrevMonth} className="text-blue-500 p-1 rounded-full hover:bg-blue-100">
                    <ChevronLeft size={buttonSize} />
                </button>
                <h2 className="text-lg font-semibold">{format(month, "MMMM yyyy")}</h2>
                <button onClick={goToNextMonth} className="text-blue-500 p-1 rounded-full hover:bg-blue-100">
                    <ChevronRight size={buttonSize} />
                </button>
            </div>
        );
    };

    return (
        <div>
            <div className="flex items-center justify-center">
                {isUpdateMode ? (
                    <h2 className="text-2xl font-semibold mb-4">
                        Update Request (
                        {DateTimeUtil.formatDateRange(timeoffToUpdate?.start_date!, timeoffToUpdate?.end_date!)})
                    </h2>
                ) : (
                    <h2 className="text-2xl font-semibold mb-4">New Request</h2>
                )}
            </div>
            <p className="text-sm text-gray-500 mb-4">Select the range of days you will be out.</p>
            <div className="bg-white px-2 rounded-lg w-full max-w-md">
                <CalendarHeader month={currentMonth} onMonthChange={setCurrentMonth} />

                <div className="flex justify-center overflow-x-auto">
                    <DayPicker
                        mode="range"
                        selected={
                            selectedDates.length === 2 ? { from: selectedDates[0], to: selectedDates[1] } : undefined
                        }
                        onSelect={handleSelect}
                        showOutsideDays={true}
                        month={currentMonth}
                        onMonthChange={setCurrentMonth}
                        hideNavigation={true}
                        classNames={{
                            ...getDefaultClassNames(), // Preserve default styles
                            selected: "bg-blue-600", // Selected date styling
                            today: "text-blue-600 font-bold", // Today's date styling
                            range_start: "text-white rounded-l-lg", // Start date styling
                            range_end: "text-white rounded-r-lg", // End date styling
                            day: "w-10 h-10", // Ensures consistent sizing
                        }}
                        components={{
                            // PreviousMonthButton,
                            // NextMonthButton,
                            MonthCaption: (props) => {
                                return <></>;
                            },
                        }}
                    />
                </div>
            </div>
            <div className="flex justify-between items-center mt-6">
                <button
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    onClick={handleClearSelection}
                    disabled={selectedDates.length === 0}
                >
                    Clear Selection
                </button>
                <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-green-600"
                    onClick={onNext}
                    disabled={selectedDates.length < 2}
                >
                    Continue
                </button>
            </div>
        </div>
    );
};

export default DatePickerStep;
